'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AnyVariables, Client } from 'urql';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
interface Props<TData, TVariables extends AnyVariables, TItem> {
  client: Client;
  query: TypedDocumentNode<TData, TVariables>;
  variablesFn: (offset: number) => TVariables;
  getItems: (data: TData | null | undefined) => TItem[];
  initialItems?: TItem[];
}
export const PAGE_SIZE = parseInt(process.env.NEXT_PUBLIC_PAGE_SIZE ?? '10', 10);
export function useInfiniteList(t0) {
  const $ = _c(21);
  const {
    client,
    query,
    variablesFn,
    getItems,
    initialItems: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const initialItems = t2;
  const [items, setItems] = useState(initialItems);
  const [nextOffset, setNextOffset] = useState(PAGE_SIZE);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  let t3;
  if ($[2] !== client || $[3] !== getItems || $[4] !== hasMore || $[5] !== isLoadingMore || $[6] !== nextOffset || $[7] !== query || $[8] !== variablesFn) {
    t3 = async () => {
      if (!hasMore || isLoadingMore) {
        return;
      }
      setIsLoadingMore(true);
      const result = await client.query(query, variablesFn(nextOffset)).toPromise();
      const newItems = getItems(result.data);
      if (!result.error) {
        if (newItems.length) {
          setItems(prev => [...prev, ...newItems]);
          setNextOffset(_temp);
          setHasMore(newItems.length === PAGE_SIZE);
        } else {
          setHasMore(false);
        }
      }
      setIsLoadingMore(false);
    };
    $[2] = client;
    $[3] = getItems;
    $[4] = hasMore;
    $[5] = isLoadingMore;
    $[6] = nextOffset;
    $[7] = query;
    $[8] = variablesFn;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const loadMore = t3;
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = t5 => {
      const newInitialItems = t5 === undefined ? [] : t5;
      setItems(newInitialItems);
      setNextOffset(PAGE_SIZE);
      setHasMore(newInitialItems.length === PAGE_SIZE);
      setIsLoadingMore(false);
    };
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const reset = t4;
  let t5;
  let t6;
  if ($[11] !== hasMore || $[12] !== isLoadingMore || $[13] !== loadMore) {
    t5 = () => {
      const scrollContainer = scrollRef.current;
      const sentinel = sentinelRef.current;
      if (!scrollContainer || !sentinel || !hasMore || isLoadingMore) {
        return;
      }
      const observer = new IntersectionObserver(t7 => {
        const [entry] = t7;
        if (entry.isIntersecting) {
          loadMore();
        }
      }, {
        root: scrollContainer,
        rootMargin: "0px 0px 500px 0px",
        threshold: 0
      });
      observer.observe(sentinel);
      return () => observer.disconnect();
    };
    t6 = [hasMore, loadMore, isLoadingMore];
    $[11] = hasMore;
    $[12] = isLoadingMore;
    $[13] = loadMore;
    $[14] = t5;
    $[15] = t6;
  } else {
    t5 = $[14];
    t6 = $[15];
  }
  useEffect(t5, t6);
  let t7;
  if ($[16] !== hasMore || $[17] !== isLoadingMore || $[18] !== items || $[19] !== loadMore) {
    t7 = {
      items,
      isLoadingMore,
      hasMore,
      scrollRef,
      sentinelRef,
      reset,
      loadMore
    };
    $[16] = hasMore;
    $[17] = isLoadingMore;
    $[18] = items;
    $[19] = loadMore;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7 as const;
}
function _temp(prev_0) {
  return prev_0 + PAGE_SIZE;
}